import { BaseApiService } from './baseApi';
import type { ApiResponse } from '../../../../types';

// Interface for l'expérience selon le template backend
export interface Experience {
  id: string;
  user_id?: string;
  company_name: string;
  position: string;
  description_fr?: string;
  description_en?: string;
  start_date: string;
  end_date?: string;
  is_current: boolean;
  location?: string;
  company_url?: string;
  technologies?: string;
  achievements?: string;
  order_index: number;
  is_visible: boolean;
  created_at: string;
  updated_at: string;
}

export interface ExperiencesFilter {
  company_name?: string;
  position?: string;
  is_current?: boolean;
  is_visible?: boolean;
  technologies?: string;
  sort?: 'start_date' | 'end_date' | 'company_name' | 'position' | 'order_index';
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface CreateExperienceRequest {
  company_name: string;
  position: string;
  description_fr?: string;
  description_en?: string;
  start_date: string;
  end_date?: string;
  is_current: boolean;
  location?: string;
  company_url?: string;
  technologies?: string;
  achievements?: string;
  is_visible: boolean;
  order_index?: number;
}

export class ExperiencesApiService extends BaseApiService {
  constructor() {
    super('/experiences');
  }

  /**
   * Fetch toutes les expériences avec filtres
   */
  async getAll(filters?: ExperiencesFilter): Promise<ApiResponse<Experience[]>> {
    const queryString = filters ? this.buildQueryString(filters) : '';
    return this.requestGet<Experience[]>(`${queryString}`, false);
  }

  /**
   * Fetch les expériences courantes
   */
  async getCurrent(): Promise<ApiResponse<Experience[]>> {
    return this.requestGet<Experience[]>('/current', false);
  }

  /**
   * Fetch les statistiques des expériences
   */
  async getStats(): Promise<ApiResponse<any>> {
    return this.requestGet<any>('/stats', false);
  }

  /**
   * Fetch une expérience par ID
   */
  async getById(id: string): Promise<ApiResponse<Experience>> {
    return this.requestGet<Experience>(`/${id}`, false);
  }

  /**
   * Créer une nouvelle expérience
   */
  async create(data: CreateExperienceRequest): Promise<ApiResponse<Experience>> {
    return this.requestPost<Experience>('', data);
  }

  /**
   * Mettre à jour une expérience
   */
  async update(id: string, data: Partial<CreateExperienceRequest>): Promise<ApiResponse<Experience>> {
    return this.requestPut<Experience>(`/${id}`, data);
  }

  /**
   * Supprimer une expérience
   */
  async deleteExperience(id: string): Promise<ApiResponse<boolean>> {
    return this.requestDelete<boolean>(`/${id}`);
  }

  /**
   * Réorganiser les expériences
   */
  async reorder(orderData: Array<{ id: string; order_index: number }>): Promise<ApiResponse<boolean>> {
  return this.requestPost<boolean>('/reorder', { experienceOrders: orderData });
  }

  /**
   * Toggle la visibilité d'une expérience
   */
  async toggleVisibility(id: string): Promise<ApiResponse<Experience>> {
    return this.requestPatch<Experience>(`/${id}/visibility`, {});
  }

  /**
   * Toggle le statut courant d'une expérience
   */
  async toggleCurrent(id: string): Promise<ApiResponse<Experience>> {
    return this.requestPatch<Experience>(`/${id}/current`, {});
  }

  /**
   * Rechercher des expériences
   */
  async search(query: string): Promise<ApiResponse<Experience[]>> {
    return this.requestGet<Experience[]>(`?search=${encodeURIComponent(query)}`, false);
  }

  /**
   * Exporter les expériences
   */
  async export(format: 'json' | 'csv' = 'json'): Promise<ApiResponse<any>> {
    return this.requestGet<any>(`/export?format=${format}`, true);
  }

  /**
   * Valider les données d'expérience
   */
  async validate(data: Partial<CreateExperienceRequest>): Promise<ApiResponse<boolean>> {
    return this.requestPost<boolean>('/validate', data);
  }
}

export const experiencesApi = new ExperiencesApiService();
